import {Text} from '@/components/atoms'
import {SelectableList} from '@/components/molecules'
import {useSegmentFolders} from '@/hooks/useSegmentFolders'

/**
 * @typedef {import('@/types/promotionTypes').SegmentDetails} SegmentDetails
 */

/**
 * Content component for displaying and selecting segments organized by folders.
 * Provides a two-panel interface: left panel shows segment folders, right panel shows segments within the selected folder.
 * Supports both individual segment selection and bulk folder selection.
 *
 * @component
 * @param {Object} props - Component props
 * @param {SegmentDetails[]} props.selectedSegments - Array of currently selected segment objects
 * @param {Function} props.setSelectedSegments - Setter function to update selected segments array
 * @returns {JSX.Element} The SegmentFolderContent component
 *
 * @example
 * <SegmentFolderContent
 *   selectedSegments={[{segmentId: '1', segmentName: 'Segment 1', segmentFolderId: 'folder1'}]}
 *   setSelectedSegments={(segments) => console.log('Updated segments:', segments)}
 * />
 */
export const SegmentFolderContent = ({selectedSegments, setSelectedSegments}) => {
  const {
    selectedSegmentFolderId,
    segmentsFoldersItems,
    segmentsItems,
    isLoadingFolders,
    isLoadingSegmentsByFolder,
    onFolderClick,
    onFolderCheckboxChange,
    onSegmentCheckboxChange,
  } = useSegmentFolders({selectedSegments, setSelectedSegments})

  return (
    <div className="flex h-full w-full">
      <div className="h-full w-1/2 overflow-y-auto border-r border-gray-600">
        <SelectableList
          title="Folder"
          items={segmentsFoldersItems || []}
          onItemClick={onFolderClick}
          onCheckboxChange={onFolderCheckboxChange}
          showSearch={true}
          searchPlaceholder="Search folders..."
          selectable={true}
          showSelectedCount={false}
          separateItems={false}
          isLoading={isLoadingFolders}
        />
      </div>
      <div className="w-1/2">
        {!selectedSegmentFolderId ? (
          <div className="flex w-full items-center justify-center">
            <Text>Please select a folder to view segments</Text>
          </div>
        ) : (
          <SelectableList
            title="Segments"
            items={segmentsItems || []}
            onCheckboxChange={onSegmentCheckboxChange}
            showSearch={true}
            searchPlaceholder="Search segments..."
            selectable={false}
            showSelectedCount={false}
            separateItems={true}
            isLoading={isLoadingSegmentsByFolder}
          />
        )}
      </div>
    </div>
  )
}
